import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
// mantine
import { ScrollArea, Select, Switch, TextInput } from '@mantine/core';
import { useDebouncedState, useDisclosure } from '@mantine/hooks';
// icons
import { Search } from 'lucide-react';
// components
import type { TAttribute, TAttributeData } from '@newo/api/gateway';
import AttributeForm from '@/pages/Attributes/components/AttributeForm';
import Drawer from '@/components/Drawer';
import NoRecords from '@/components/NoRecords';
import GroupNavigation from '@/pages/Attributes/components/GroupNavigation';
import Navigation from '@/pages/Attributes/components/Navigation';
import GroupTitle from '@/pages/Attributes/components/GroupTitle';
import Attribute from '@/pages/Attributes/components/Attribute';
import ContextMenu from '@/pages/Attributes/Projects/components/ContextMenu';
// utils
import { useProjectAttributesQuery } from '@/services/bff/bff.queries';
import {
    useCreateProjectAttributesMutation,
    useUpdateProjectAttributesMutation,
    useDeleteProjectAttributesMutation
} from '@/services/designer/project_attributes/project_attributes.queries';
import { useProjectsQuery } from '@/services/designer/projects/projects.queries';
import { PATHS } from '@/const';
import { openConfirmDeleteModal } from '@/utils/ui.tsx';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';
import { sortByNumericPrefix, sortByNumericPrefixProperty } from '@/utils';
import { useAppStore } from '@/store/app';

const DEBOUNCE_TIMEOUT = 200;

const Projects = () => {
    const navigate = useNavigate();
    const { group: groupParameter, projectId } = useParams();
    const group = groupParameter
        ? decodeURIComponent(groupParameter)
        : undefined;

    const store = useAppStore();
    const [selectedAttribute, setSelectedAttribute] = useState<
        TAttribute | undefined
    >();
    const [isOpened, handlers] = useDisclosure();
    const [searchTerm, setSearchTerm] = useDebouncedState('', DEBOUNCE_TIMEOUT);

    const { data, refetch } = useProjectAttributesQuery(projectId, {
        query: searchTerm,
        include_hidden: store.isAttrsHiddenShown
    });

    const { data: projects } = useProjectsQuery();
    const { mutateAsync: createAttribute } =
        useCreateProjectAttributesMutation();
    const { mutate: deleteAttribute } = useDeleteProjectAttributesMutation();
    const { mutateAsync: updateAttribute } =
        useUpdateProjectAttributesMutation();

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.currentTarget.value);
        navigate(`${PATHS.attributes.root}/projects/${projectId ?? ''}`);
    };

    useEffect(() => {
        if (!projectId && projects?.length) {
            navigate(`${PATHS.attributes.root}/projects/${projects[0].id}`);
        }
    }, [projectId, projects, navigate]);

    const handleOpen = () => {
        handlers.open();
    };

    const handleClose = () => {
        handlers.close();
        setSelectedAttribute(undefined);
    };

    const handleProjectSelect = (value: string | null) => {
        navigate(`${PATHS.attributes.root}/projects/${value ?? ''}`);
    };

    const handleAttributeSubmit = async ({
        attributeId,
        data: formData
    }: {
        attributeId?: string;
        data: TAttributeData;
    }) => {
        if (!projectId) {
            return;
        }
        await (attributeId
            ? updateAttribute({ projectId, attributeId, data: formData })
            : createAttribute({ projectId, data: formData }));
        handleClose();
        refetch();
    };

    const handleEdit = (attribute: TAttribute) => {
        handleOpen();
        setSelectedAttribute(attribute);
    };

    const handleDelete = (attribute: TAttribute) => {
        openConfirmDeleteModal({
            title: `Delete ${attribute.idn} attribute?`,
            children: `Are you sure you want to delete ${attribute.idn} attribute?`,
            confirmLabel: 'Delete Attribute',
            onConfirm: () => {
                if (!projectId) {
                    return;
                }
                deleteAttribute(
                    { attributeId: attribute.id, projectId },
                    {
                        onSuccess: () => {
                            refetch();
                        }
                    }
                );
            }
        });
    };

    const groups = sortByNumericPrefix(data?.groups ?? []);

    const items = useMemo(() => {
        const attributes = data?.attributes ?? [];
        let filteredAttributes: TAttribute[];

        if (group === 'Unassigned') {
            filteredAttributes = attributes.filter(
                (attribute) =>
                    attribute.group === 'unassigned' || !attribute.group
            );
        } else if (group) {
            filteredAttributes = attributes.filter(
                (attribute) => attribute.group === group
            );
        } else {
            filteredAttributes = attributes;
        }

        return sortByNumericPrefixProperty(filteredAttributes, 'title');
    }, [data?.attributes, group]);

    // TODO: Move unassigned to BE
    const unassignedItems = data?.attributes.filter(
        (attribute) => attribute.group === 'unassigned' || !attribute.group
    );

    const options = useMemo(() => {
        return projects?.map((project) => ({
            label: project.idn,
            value: project.id
        }));
    }, [projects]);

    return (
        <div className="h-full w-full">
            <PageContainer className="pt-6 !grid !gap-4 !h-full !overflow-hidden !grid-cols-[300px_1fr]">
                <div className="flex flex-col gap-4 overflow-hidden">
                    <Navigation className="mb-1" value="projects" />
                    <Select
                        data={options}
                        label="Select Project"
                        placeholder="Select Project"
                        value={projectId}
                        onChange={handleProjectSelect}
                    />
                    <ScrollArea h="100%" scrollbars="y" type="hover">
                        {projectId ? (
                            <GroupNavigation
                                withAll
                                baseUrl={`${PATHS.attributes.root}/projects/${projectId}`}
                                groups={groups}
                                selectedGroup={group}
                                withUnassigned={!!unassignedItems?.length}
                            />
                        ) : undefined}
                    </ScrollArea>
                </div>
                <div className="overflow-hidden pb-20">
                    <div className="flex items-center gap-8  pb-4">
                        <TextInput
                            className="w-1/2"
                            defaultValue={searchTerm}
                            leftSection={<Search />}
                            placeholder="Search by idn, title, group"
                            size="sm"
                            onChange={handleSearchChange}
                        />
                        <Switch
                            checked={store.isAttrsHiddenShown}
                            className="ml-auto"
                            label="Show Hidden"
                            onChange={() => {
                                store.toggleAttrsHiddenShown();
                            }}
                        />

                        <ContextMenu
                            isDisabled={!projectId}
                            onAdd={handleOpen}
                        />
                    </div>

                    <ScrollArea h="100%" scrollbars="y" type="hover">
                        <div className="flex flex-col gap-4 pr-4">
                            <GroupTitle>{group ?? 'all'}</GroupTitle>
                            {items.length === 0 ? <NoRecords /> : undefined}
                            {items.map((item) => (
                                <Attribute
                                    attribute={item}
                                    key={item.id}
                                    onDelete={handleDelete}
                                    onEdit={handleEdit}
                                    onSave={handleAttributeSubmit}
                                />
                            ))}
                        </div>
                    </ScrollArea>
                </div>
            </PageContainer>
            <Drawer
                withOverlay
                name="customer-attributes"
                opened={isOpened}
                position="right"
                size="md"
                onClose={handleClose}
            >
                <AttributeForm
                    attribute={selectedAttribute}
                    groups={groups}
                    onClose={handleClose}
                    onSubmit={handleAttributeSubmit}
                />
            </Drawer>
        </div>
    );
};

export default Projects;
